import React, { useEffect } from "react";
import Footer from "../../components/common/Footer";
import Header from "../../components/common/Header";
import NavBar from "../../components/common/NavBar";
import {
  ProposerCard,
  ProposerCardLoading,
} from "../../components/proposer/ProposerCard";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { proposerFormType } from "../../lib/proposerAPI";
import { useState } from "react";
import { useAllProposersData } from "../../lib/hooks";
import SearchInput from "../../components/common/SearchInput";
import LoadingError from "../../components/common/LoadingError";
import ProposerSortMenu from "../../components/proposer/ProposerSortMenu";
import SideFilterFrame from "../../components/common/SideFilterFrame";
import ProposerFilter from "../../components/proposer/ProposerFilter";
import { FormatListBulletedSharp, GridView } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import {
  proposerColumns,
  allProposerColumns,
} from "../../components/analytics/GridViewColumns";

const cards = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const ProposersIndex = () => {
  const [formData, setFormData] = useState<proposerFormType>({
    num_per_page: 12,
    page_number: 1,
    order_by: "user_name",
    ordering: "asc",
    fund_id: "All",
    funded_status: "All",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [formData.page_number, formData.num_per_page]);

  const { loading, error, proposerData } = useAllProposersData(formData);

  const changeNumberPerPage = (event: SelectChangeEvent) => {
    let numberPerPage = parseInt(event.target.value);
    setFormData({
      ...formData,
      num_per_page: numberPerPage,
      page_number: 1,
    });
  };

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const sortPage = (sortData: {
    order_by: string;
    ordering: "asc" | "desc";
  }) => {
    setFormData((prevState) => ({
      ...prevState,
      order_by: sortData.order_by,
      ordering: sortData.ordering,
      page_number: 1,
    }));
  };

  const searchPage = (searchData: string) => {
    setFormData((prevState) => ({
      ...prevState,
      proposer_name: searchData,
      page_number: 1,
    }));
  };

  const setFundStatus = (statusData: string) => {
    setFormData({ ...formData, funded_status: statusData, page_number: 1 });
  };

  const formDataHandler = (submittedFormData: any) => {
    setFormData({ ...formData, ...submittedFormData, page_number: 1 });
  };

  const [viewStyle, setViewStyle] = useState("grid");

  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewStyle: string
  ) => {
    setViewStyle(viewStyle === "list" ? "grid" : "list");
  };

  let proposerRows: any = [];
  let extraProposerRows: any = [];
  proposerData.items.forEach((proposer: any) => {
    proposerRows.push({
      ranking: proposer.ranking,
      name: {
        user_id: proposer.user_id,
        user_name: proposer.user_name,
      },
      avg_rating: proposer.quick_stats_data.average_rating.toFixed(2),
      num_proposals: proposer.quick_stats_data.number_of_proposals,
      funded_proposals: proposer.quick_stats_data.funded_proposals,
      amount_requested: proposer.quick_stats_data.amount_requested,
      funded_amount: proposer.quick_stats_data.funded_amount,
      funding_percentage:
        (proposer.quick_stats_data.funding_percentage * 100).toFixed(2) + "%",
      votes: proposer.quick_stats_data.total_result_votes,
      id: proposer.user_id,
      conversion_percentage:
        (proposer.quick_stats_data.conversion_percentage * 100).toFixed(2) +
        "%",
    });
    extraProposerRows.push({
      highest_amount_requested:
        proposer.quick_stats_data.highest_amount_requested,
      total_up_votes: proposer.quick_stats_data.total_up_votes,
      total_down_votes: proposer.quick_stats_data.total_down_votes,
      highest_rating:
        proposer.quick_stats_data.highest_proposal_rating.toFixed(2),
    });
  });

  let allProposerRows: any = [];
  proposerRows.forEach((item: any, index: number) => {
    allProposerRows.push({
      ...item,
      ...extraProposerRows[index],
    });
  });

  const [showExtraColumns, setShowExtraColumns] = useState<boolean>(false);
  const toggleShowExtraColumn = () => {
    setShowExtraColumns((prevState: boolean) => !prevState);
  };

  return (
    <>
      <NavBar />
      <Header pageName="Proposers" />
      <SideFilterFrame
        headerName="Proposers"
        goToTop={goToTop}
        filter={
          <ProposerFilter
            setFundStatus={setFundStatus}
            formDataHandler={(submittedFormData: any) =>
              formDataHandler(submittedFormData)
            }
          />
        }
        sort={
          <>
            <ToggleButtonGroup
              color="primary"
              value={viewStyle}
              exclusive
              onChange={handleViewChange}
              size="small"
            >
              <ToggleButton value="list">
                <FormatListBulletedSharp fontSize="small" />
              </ToggleButton>
              <ToggleButton value="grid">
                <GridView fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            <ProposerSortMenu sortPage={sortPage} />
          </>
        }
        search={
          <SearchInput
            searchPage={searchPage}
            placeholder="Search by Proposer"
          />
        }
      >
        <>
          {loading && viewStyle === "grid" && (
            <>
              <Grid container spacing={4} sx={{ justifyContent: "flex-start" }}>
                {cards.map((item) => (
                  <Grid item key={item} xs={12} md={6} xl={4}>
                    <ProposerCardLoading />
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {viewStyle === "list" && error === null && (
            <Box sx={{ width: "100%", paddingBottom: "100px" }}>
              <Button variant="text" onClick={toggleShowExtraColumn}>
                {showExtraColumns ? "hide extra columns" : "show extra columns"}
              </Button>
              <DataGrid
                componentsProps={{
                  pagination: { classes: null },
                }}
                rows={showExtraColumns ? allProposerRows : proposerRows}
                columns={
                  showExtraColumns ? allProposerColumns : proposerColumns
                }
                pageSize={formData.num_per_page}
                page={
                  formData.page_number - 1 >= 0 ? formData.page_number - 1 : 0
                }
                rowsPerPageOptions={[9, 12, 24, 36, 72, 99]}
                disableSelectionOnClick
                autoHeight
                disableColumnFilter
                loading={loading}
                onPageSizeChange={(newVal) => {
                  setFormData({
                    ...formData,
                    num_per_page: newVal,
                    page_number: 1,
                  });
                }}
                onPageChange={(newVal) => {
                  setFormData({
                    ...formData,
                    page_number: newVal + 1,
                  });
                }}
                paginationMode="server"
                rowCount={proposerData.number_of_rows}
              />
            </Box>
          )}

          {!loading && error && (
            <>
              {/* <Alert severity="error">{error}</Alert> */}
              <LoadingError h1="No Proposer Data" />
            </>
          )}

          {!loading && error === null && viewStyle === "grid" && (
            <>
              <Grid container spacing={4} sx={{ justifyContent: "flex-start" }}>
                {Array.isArray(proposerData.items) &&
                  proposerData.items.map((item, index) => (
                    <Grid item key={index} xs={12} md={6} xl={4}>
                      <ProposerCard {...item} />
                    </Grid>
                  ))}
              </Grid>
              <div className="pullRight">
                <Box className="paginationContainer">
                  <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
                    <InputLabel id="demo-select-small">Per Page:</InputLabel>
                    <Select
                      labelId="demo-select-small"
                      id="demo-select-small"
                      name="numberPerPage"
                      value={formData.num_per_page.toString()}
                      label="Per Page"
                      onChange={changeNumberPerPage}
                    >
                      <MenuItem value={"12"}>12</MenuItem>
                      <MenuItem value={"24"}>24</MenuItem>
                      <MenuItem value={"36"}>36</MenuItem>
                      <MenuItem value={"48"}>48</MenuItem>
                      <MenuItem value={"60"}>60</MenuItem>
                    </Select>
                  </FormControl>
                  <Pagination
                    count={Math.ceil(
                      proposerData.number_of_rows / formData.num_per_page
                    )}
                    onChange={(
                      event: React.ChangeEvent<unknown>,
                      value: number
                    ) => {
                      setFormData({
                        ...formData,
                        page_number: value,
                      });
                    }}
                    page={formData.page_number}
                  />
                </Box>
              </div>
            </>
          )}
        </>
      </SideFilterFrame>
      <Footer />
    </>
  );
};

export default ProposersIndex;
